import React from "react"

import { TimeInput } from "@tenforce/product-fields"

import { ITimeField } from "../.."
import { updateField } from "../../slice"
import { useAppDispatch } from "../../../../hooks"
import { parseUserInputForUsTime } from "../../../../apis/utils/dateTimeParsing"
import HTMLAndInternalLinkParser from "../common/HTMLAndInternalLinkParser"
import { Labels } from "../../../../util/Translations"
import { DateTime } from "luxon"

export interface Props {
    field: ITimeField
}

export const TimeField = ({ field }: Props): JSX.Element => {
    const dispatch = useAppDispatch()
    const {
        position: _position,
        odataType: _odataType,
        value,
        isUsTime,
        ...fieldProps
    } = field

    const onSave = (newValue: string): void => {
        let valueToSave
        if (!newValue) {
            valueToSave = null
        } else {
            valueToSave = isUsTime
                ? parseUserInputForUsTime(newValue)
                : newValue

            if (value) {
                const localDateTime = DateTime.fromISO(value)
                let systemDateTime = DateTime.fromFormat(valueToSave, "HH:mm", {
                    zone: "system",
                })
                if (!systemDateTime.isValid) {
                    // In case the user manually entered a time in the H:mm format.
                    systemDateTime = DateTime.fromFormat(valueToSave, "H:mm", {
                        zone: "system",
                    })
                }
                const newValue = localDateTime.set({
                    hour: systemDateTime?.hour,
                    minute: systemDateTime?.minute,
                })
                valueToSave = newValue.toISO()
            } else {
                valueToSave = DateTime.fromFormat(valueToSave, "HH:mm").toISO()
            }
        }

        if (value === valueToSave) {
            return
        }

        dispatch(
            updateField({
                field,
                newValue: valueToSave,
            }),
        )
    }

    const {
        ClickHereToEdit,
        PleaseEnterValidTime,
        PleaseEnterValidTimeUs,
        RequiredFieldErrorMessage,
    } = Labels

    let finalValue = ""
    if (value) {
        // We need to trick a Javascript date into ignoring the difference between the user timezone and instead use the profile's one.
        const localDateTime = DateTime.fromISO(value)
        const dateTime = localDateTime.setZone("system", {
            keepLocalTime: true,
        })
        finalValue = isUsTime
            ? dateTime.toFormat("h:mm a")
            : dateTime.toFormat("HH:mm")
    }

    return (
        <TimeInput
            {...fieldProps}
            description={
                <HTMLAndInternalLinkParser
                    content={(field?.description as string) ?? ""}
                />
            }
            value={finalValue}
            isUsTime={isUsTime}
            placeholder={ClickHereToEdit}
            validationErrorText={
                isUsTime ? PleaseEnterValidTimeUs : PleaseEnterValidTime
            }
            mandatoryErrorText={RequiredFieldErrorMessage}
            onSave={onSave}
        />
    )
}

export default TimeField
